import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SettingsIcon from '@mui/icons-material/Settings';
import StopIcon from '@mui/icons-material/Stop';
import axios from 'axios';

const ModelSettingsButton = ({ model, onRuntimeAction, onDelete }) => {
  const [openModal, setOpenModal] = useState(false);
  const [showInPublicBlueprint, setShowInPublicBlueprint] = useState(
    Boolean(model.info?.public),
  );
  const runtimeStatus = model.runtime_status || 'STOPPED';
  const buildStatus = model.build_status || 'PENDING';
  const isBuildReady = buildStatus === 'READY';
  const isRunning = runtimeStatus === 'RUNNING';
  const isTransitioning = runtimeStatus === 'STARTING' || runtimeStatus === 'STOPPING';

  const handleDelete = () => {
    if (window.confirm(`Delete ${model.name}?`)) {
      setOpenModal(false);
      onDelete();
    }
  };

  const handlePublicBlueprintChange = async (event) => {
    const publicValue = event.target.checked;
    setShowInPublicBlueprint(publicValue);

    try {
      await axios.patch(`/v4p_models/${model.id}`, {
        info: {
          ...(model.info || {}),
          public: publicValue,
        },
      });
    } catch (error) {
      setShowInPublicBlueprint(!publicValue);
      window.alert(error.response?.data?.detail || 'Could not update public blueprint visibility');
    }
  };

  return (
    <>
      <Tooltip title="Model settings">
        <IconButton
          aria-label={`Settings for ${model.name}`}
          onClick={() => setOpenModal(true)}
          size="small"
          sx={{ color: '#16a4b0' }}
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            p: 4,
            pt: 6,
            borderRadius: 3,
            width: { xs: '90%', sm: '40%' },
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h3" fontWeight={700} color="black">
            Model Settings
          </Typography>
          <Typography variant="h6" color="rgb(158, 151, 151)" mt={2}>
            Edit the model
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} color="black">
              Show in public blueprint
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showInPublicBlueprint}
                  onChange={handlePublicBlueprintChange}
                  sx={{ color: '#16a4b0', '&.Mui-checked': { color: '#16a4b0' } }}
                />
              }
              label="Show this model in the public blueprint"
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} color="black" mb={1}>
              Start/stop container
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => onRuntimeAction(model, 'start')}
                disabled={isRunning || !isBuildReady || isTransitioning}
                startIcon={<PlayArrowIcon />}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  borderRadius: '12px',
                  backgroundColor: '#16a4b0',
                  color: 'white',
                  boxShadow: 'none',
                  '&:hover': { backgroundColor: '#128d98', boxShadow: 'none' },
                }}
              >
                Start
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => onRuntimeAction(model, 'stop')}
                disabled={!isRunning || isTransitioning}
                startIcon={<StopIcon />}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  borderRadius: '12px',
                  color: '#c62828',
                  borderColor: '#e57373',
                  '&:hover': { backgroundColor: '#fff5f5', borderColor: '#e57373' },
                }}
              >
                Stop
              </Button>
            </Box>
          </Box>

          {onDelete && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={600} color="black" mb={1}>
                Delete the container
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  borderRadius: '12px',
                  color: '#c62828',
                  borderColor: '#e57373',
                  '&:hover': { backgroundColor: '#fff5f5', borderColor: '#c62828' },
                }}
              >
                Delete container
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={() => setOpenModal(false)}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                minHeight: 40,
                textTransform: 'none',
                borderRadius: '12px',
                color: '#16a4b0',
                borderColor: '#16a4b0',
                '&:hover': { backgroundColor: '#f0f0f0', borderColor: '#16a4b0' },
              }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default ModelSettingsButton;